import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  FormLabel,
  MenuItem,
  Select,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateEmail,
  validatePhoneNumber,
  validationMessages,
} from "../../actions/validations";
import { YesOrNo } from "../../../constants/YesOrNo";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const RoleSetup = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [roleCode, setRoleCode] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameError2, setNameError2] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  //const [active, setActive] = useState<boolean>(true);
  const [GolfRoles, setGolfRoles] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateInput = () => {
    if (!roleCode) {
      setAlertMessage("Role code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!roleName) {
      setAlertMessage("Role name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    if (
      roleCode &&
      !validateString(roleCode, validationLength.roleCode, validationType.CODE)
    ) {
      setNameError(true);
      return false;
    }
    setNameError2(false);
    if (
      roleName &&
      !validateString(roleName, validationLength.roleName, validationType.SPACE)
    ) {
      setNameError2(true);
      return false;
    }

    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [roleCode, roleName]);

  const updateGolfRoles = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        GolfRoles.filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ROLECODE,
        roleCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setNameError2(false);
    setGolfRoles((prev: any) => {
      return prev.map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editGolfRoles(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${GolfPropertyEnum.roleSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfRoles = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(GolfRoles, DNDFieldsEnum.ROLECODE, roleCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfRoles((prev: any) => {
      return updateData(
        prev !== null
          ? [
              ...prev,
              {
                id: GolfRoles.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfRoles(null);
    setEditClicked(false);
  };

  const editGolfRoles = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setRoleCode(list?.["Role Code"]);
    setRoleName(list?.["Role Name"]);
    //setActive(list?.Active);
  };

  const deleteGolfRoles = (id: number) => {
    setGolfRoles(deleteItem(GolfRoles, id));
  };

  useEffect(() => {
    fieldValues && setGolfRoles(fieldValues);
  }, []);

  useEffect(() => {
    data(GolfRoles);
    editClicked &&
      setSelectedId(checkData(GolfRoles, selectedId) ? selectedId : null);
  }, [GolfRoles]);

  const payload = {
    "Role Code": roleCode,
    "Role Name": roleName,
    //Active: active,
  };
  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            Role Setup
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.roleSetupHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editGolfRoles(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editGolfRoles(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>
        <FormLabel component="legend">
          Role Code
          <Tooltip title={tooltipComponent(golfTooltipHeaders.roleCode)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </FormLabel>
        <TextField
          id="roleCode"
          variant="outlined"
          placeholder="Role Code"
          helperText={nameError ? validationMessages.roleCode : ""}
          error={nameError}
          value={roleCode ?? ""}
          onChange={(e: any) => {
            setRoleCode(e.target.value);
          }}
        />
        <FormLabel component="legend">
          Role Name
          <Tooltip title={tooltipComponent(golfTooltipHeaders.roleName)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </FormLabel>
        <TextField
          id="roleName"
          variant="outlined"
          placeholder="Role Name"
          value={roleName ?? ""}
          helperText={nameError2 ? validationMessages.roleName : ""}
          error={nameError2}
          onChange={(e: any) => {
            setRoleName(e.target.value);
          }}
        />
        {/* <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch checked={active} onChange={() => setActive(!active)} />
            }
            label="9 Holes?"
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.hole9)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack> */}
        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateGolfRoles() : addGolfRoles())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Role
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Role Setup Available</Typography>
          <Stack direction="column" gap="10px">
            {GolfRoles ? (
              <DraggableItemList
                itemData={GolfRoles}
                onOrderUpdate={(order) => setGolfRoles(order)}
                onEdit={(info) => editGolfRoles(info)}
                onDelete={(id) => deleteGolfRoles(id)}
                code={DNDFieldsEnum.ROLECODE}
                name={DNDFieldsEnum.ROLENAME}
              />
            ) : (
              <Typography variant="body2">
                No Role Setup Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default RoleSetup;
